/**
 * Camera rigs behind one interface (M2 Task 6): v1 ships the classic top-down rig; the chase and bridge rigs are
 * ported for v1.1 (GDD §2). A rig keeps its own state, steps with the simulation and poses the camera between the
 * last two steps, writing into a caller-owned pose (no allocation).
 */
import type { FlightState } from '../sim/flight.ts';

export type RigName = 'top' | 'chase' | 'bridge';

export interface RigPose {
  pos: [number, number, number];
  target: [number, number, number];
  /** Roll about the view axis after looking at the target (rad). */
  roll: number;
  /** Vertical field of view (degrees). */
  fov: number;
  /** Where the sun and its shadow area are centred (x, z), per rig as in the concept (L1461, L1470, L1385). */
  sun: [number, number];
}

export interface CameraRig {
  readonly name: RigName;
  /** One fixed step, after the flight step. */
  step(flight: FlightState, dt: number): void;
  /** Floating-origin shift: moves the rig's own positions, the previous step's too. */
  shiftZ(dz: number): void;
  /** The camera `alpha` of the way from the previous step to this one; `flight` is already interpolated. */
  pose(flight: FlightState, alpha: number, clock: number, out: RigPose): RigPose;
  /** A cut: the jet jumped (a checkpoint restart), so the rig settles on it at once, with nothing to interpolate. */
  cut?(flight: FlightState): void;
}

export function createRigPose(): RigPose {
  return { pos: [0, 0, 0], target: [0, 0, 0], roll: 0, fov: 40, sun: [0, 0] };
}
